"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner"
import { useChangeTracker } from "../../hooks/useChangeTracker"
import { useLocationData } from "../../hooks/useLocationData"
import { useUserDetailsApi } from "../../hooks/useUserDetailsApi"
import { ACCOUNT_CONSTANTS, DEFAULT_USER_DATA } from "../../constants/account-details"
import { COMPANY_SIZE_OPTIONS, INDUSTRY_OPTIONS, COUNTRY_OPTIONS } from "../../constants/register"
import { useToast } from "../../context/ToastContext";

import type { UserDetails, FormState } from "../../types/account-details"

const UserDetailsForm: React.FC = () => {
  const {
    data: userDetails,
    changedFields,
    hasChanges,
    updateField,
    getChangedData,
    resetChanges,
    updateOriginalData,
  } = useChangeTracker(DEFAULT_USER_DATA)

  const { availableStates, availableCities } = useLocationData(userDetails.country, userDetails.state)
  const { loadingStates, fetchUserDetails, updateUserDetailsApi } = useUserDetailsApi()
  const { error, success } = useToast();

  const [formState, setFormState] = useState<FormState>({
    loading: false,
    error: null,
    success: null,
  })

  const [isInitialized, setIsInitialized] = useState(false)

  // Load user details on component mount
  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        setFormState({ loading: false, error: null, success: null })

        const userData = await fetchUserDetails()
        if (userData) {
          updateOriginalData(userData)
        }
      } catch (err: any) {
        console.error("Failed to load user details:", err)
        error(err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.USER_FETCH_FAILED)
        setFormState({
          loading: false,
          error: err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.USER_FETCH_FAILED,
          success: null,
        })
      } finally {
        setIsInitialized(true)
      }
    }

    if (!isInitialized) {
      loadUserDetails()
    }
  }, [fetchUserDetails, updateOriginalData, isInitialized])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target
      updateField(name as keyof UserDetails, value)

      // Reset dependent fields when parent changes
      if (name === "country") {
        updateField("state", "")
        updateField("city", "")
      } else if (name === "state") {
        updateField("city", "")
      }

      // Clear messages when user starts typing
      if (formState.error || formState.success) {
        setFormState((prev) => ({ ...prev, error: null, success: null }))
      }
    },
    [updateField, formState.error, formState.success],
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!hasChanges) {
        setFormState((prev) => ({ ...prev, error: ACCOUNT_CONSTANTS.MESSAGES.NO_CHANGES }))
        return
      }

      try {
        const changedData = getChangedData()
        const response = await updateUserDetailsApi(changedData)

        if (response) {
          const successMessage = ACCOUNT_CONSTANTS.SUCCESS_MESSAGES.DETAILS_UPDATED.replace(
            "{count}",
            changedFields.size.toString(),
          )
          success("Profile Updated Successfully")

          setFormState({
            loading: false,
            error: null,
            success: successMessage,
          })

          // Update the original data to reflect the changes
          updateOriginalData(userDetails)

          // Clear success message after 3 seconds
          setTimeout(() => {
            setFormState((prev) => ({ ...prev, success: null }))
          }, 3000)
        }
      } catch (err: any) {
        error(err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.USER_FETCH_FAILED)
        console.error("Error updating user details:", error)
        setFormState({
          loading: false,
          error: err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.DETAILS_UPDATE_FAILED,
          success: null,
        })
      }
    },
    [hasChanges, getChangedData, updateUserDetailsApi, changedFields.size, updateOriginalData, userDetails],
  )

  const handleRetryLoad = useCallback(async () => {
    try {
      setFormState({ loading: false, error: null, success: null })

      const userData = await fetchUserDetails()
      if (userData) {
        updateOriginalData(userData)
        setFormState({
          loading: false,
          error: null,
          success: ACCOUNT_CONSTANTS.SUCCESS_MESSAGES.USER_LOADED,
        })

        // Clear success message after 2 seconds
        setTimeout(() => {
          setFormState((prev) => ({ ...prev, success: null }))
        }, 2000)
      }
    } catch (err: any) {
      error(err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.USER_FETCH_FAILED)
      setFormState({
        loading: false,
        error: err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.USER_FETCH_FAILED,
        success: null,
      })
    }
  }, [fetchUserDetails, updateOriginalData])

  const renderFormField = (field: any) => {
    const isChanged = changedFields.has(field.name as keyof UserDetails)
    const fieldValue = userDetails[field.name as keyof UserDetails]

    if (field.type === "select") {
      return (
        <div key={field.name} className={`form-group ${field.fullWidth ? "full-width" : ""}`}>
          <label htmlFor={field.name} className="form-label">
            {field.label}
            {isChanged && <span className="changed-indicator">*</span>}
          </label>
          <select
            id={field.name}
            name={field.name}
            value={fieldValue}
            onChange={handleInputChange}
            className={`form-input form-select ${isChanged ? "changed" : ""} ${field.disabled ? "disabled" : ""}`}
            disabled={field.disabled || loadingStates.fetchingUser || loadingStates.updatingDetails}
            required={field.required}
          >
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {field.options?.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {field.hint && <small className="form-hint">{field.hint}</small>}
        </div>
      )
    }

    return (
      <div key={field.name} className={`form-group ${field.fullWidth ? "full-width" : ""}`}>
        <label htmlFor={field.name} className="form-label">
          {field.label}
          {isChanged && <span className="changed-indicator">*</span>}
        </label>
        <input
          id={field.name}
          name={field.name}
          type={field.inputType || "text"}
          value={fieldValue}
          onChange={handleInputChange}
          className={`form-input ${isChanged ? "changed" : ""} ${field.readonly ? "readonly" : ""}`}
          readOnly={field.readonly}
          disabled={loadingStates.fetchingUser || loadingStates.updatingDetails}
          required={field.required}
        />
        {field.hint && <small className="form-hint">{field.hint}</small>}
      </div>
    )
  }

  const formFields = [
    {
      name: "firstName",
      label: "First Name",
      required: true,
    },
    {
      name: "lastName",
      label: "Last Name",
      required: true,
    },
    {
      name: "email",
      label: "Email ID",
      inputType: "email",
      readonly: true,
      hint: ACCOUNT_CONSTANTS.MESSAGES.EMAIL_READONLY,
    },
    {
      name: "companyName",
      label: "Company Name",
    },
    {
      name: "companySize",
      label: "Company Size",
      type: "select",
      options: COMPANY_SIZE_OPTIONS.slice(1),
      placeholder: "Select Company Size",
    },
    {
      name: "industry",
      label: "Industry",
      type: "select",
      options: INDUSTRY_OPTIONS.slice(1),
      placeholder: "Select Industry",
    },
    {
      name: "country",
      label: "Country",
      type: "select",
      options: COUNTRY_OPTIONS.slice(1),
      placeholder: "Select Country",
    },
    {
      name: "state",
      label: "State",
      type: "select",
      options: availableStates,
      placeholder: "Select State",
      disabled: !userDetails.country,
    },
    {
      name: "city",
      label: "City",
      type: "select",
      options: availableCities,
      placeholder: "Select City",
      disabled: !userDetails.state,
      fullWidth: true,
    },
  ]

  // Show loading state while fetching initial data
  if (loadingStates.fetchingUser && !isInitialized) {
    return (
      <div className="form-section user-details-section">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <h3>Loading User Details...</h3>
          <p>Please wait while we fetch your information.</p>
        </div>
      </div>
    )
  }

  // Show error state if initial load failed
  if (formState.error && !isInitialized) {
    return (
      <div className="form-section user-details-section">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load User Details</h3>
          <p>{formState.error}</p>
          <button onClick={handleRetryLoad} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="form-section user-details-section">
      <div className="section-header">
        <h3 className="section-title">Personal Information</h3>
        {hasChanges && (
          <div className="changes-indicator">
            <span className="changes-count">{changedFields.size} unsaved change(s)</span>
            <button type="button" onClick={resetChanges} className="btn btn-text">
              Reset
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">{formFields.map(renderFormField)}</div>

        <div className="form-actions">
          <button type="submit" disabled={!hasChanges || loadingStates.updatingDetails} className="btn btn-primary">
            {loadingStates.updatingDetails ? (
              <>
                <LoadingSpinner size="small" />
                Saving Changes...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>

        {formState.error && (
          <div className="form-message error">
            <span className="message-icon">⚠️</span>
            {formState.error}
            {isInitialized && (
              <button onClick={handleRetryLoad} className="retry-button">
                Retry
              </button>
            )}
          </div>
        )}

        {formState.success && (
          <div className="form-message success">
            <span className="message-icon">✅</span>
            {formState.success}
          </div>
        )}
      </form>
    </div>
  )
}

export default UserDetailsForm
